import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Container, Row, Table } from "reactstrap";
import { Col } from "react-bootstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { useLoader } from '../../_helper/LoaderContext';
import { addPackage, masterTest, masterTests } from './service/test-service';
import { toast } from 'react-toastify';
import { Link, useNavigate } from "react-router-dom";

export default function AddPackage() {
    const { t } = useTranslation();

    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedTests, setSelectedTests] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [total, setTotal] = useState(0);
    const [originalOptions, setOriginalOptions] = useState([]);
    const [formData, setFormData] = useState({
        packageName: "",
        packageCode: ""
    });
    const [errors, setErrors] = useState({});
    const dropdownRef = useRef(null);
    const { setLoading } = useLoader();
    const navigate = useNavigate();

    useEffect(() => {
        getMaster()
    }, [])

    // test master 
    const getMaster = async () => {
        setLoading(true)
        try {
            const res = await masterTests()
            if (res.data.success == true) {
                const data = res.data.data.tests.map((item) => ({
                    name: item.name,
                    price: item.cost || item.price,
                    id: item._id
                }))
                setOptions(data);
                setOriginalOptions(data);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    // 🔎 Handle Search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);

        if (value.trim() === "") {
            // Show only unselected items
            const filtered = originalOptions.filter(
                opt => !selectedTests.some(sel => sel.id === opt.id)
            );
            setOptions(filtered);
            return;
        }

        const filtered = originalOptions.filter(
            opt =>
                opt.name.toLowerCase().includes(value.toLowerCase()) &&
                !selectedTests.some(sel => sel.id === opt.id)
        );

        setOptions(filtered);
    };

    const handleCheckboxSelect = (option) => {
        let updated;

        if (selectedTests.some(item => item.id === option.id)) {
            // REMOVE FROM SELECTED
            updated = selectedTests.filter(item => item.id !== option.id);
        } else {
            // ADD TO SELECTED
            updated = [...selectedTests, option];
        }

        setSelectedTests(updated);

        // Update total
        setTotal(updated.reduce((sum, item) => sum + item.price, 0));

        // Update dropdown option list (hide selected)
        const remaining = originalOptions.filter(
            opt => !updated.some(sel => sel.id === opt.id)
        );

        setOptions(remaining);
    };

    // ✅ Handle Select
    const handleSelect = (option) => {
        if (!selectedTests.some(test => test.name === option.name)) {
            const updated = [...selectedTests, option];
            setSelectedTests(updated);
            const remainingOptions = options.filter(item =>
                !updated.some(sel => sel.name === item.name)
            );
            setOptions(remainingOptions);
            setTotal(updated.reduce((sum, item) => sum + item.price, 0));
        }
        setIsOpen(false);
        setSearch("");
    };

    // ❌ Delete Test
    const handleDelete = (index) => {
        const deletedItem = selectedTests[index];
        const updated = selectedTests.filter((_, i) => i !== index);
        setSelectedTests(updated);
        setOptions([...options, deletedItem]);
        setTotal(updated.reduce((sum, item) => sum + item.price, 0));
    };

    // 📦 Handle Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 🚨 Validation Logic
    const validateForm = () => {
        const newErrors = {};
        if (!formData.packageName.trim()) newErrors.packageName = t("Package name is required");
        if (!formData.packageCode.trim()) newErrors.packageCode = t("Package code is required");
        if (selectedTests.length === 0) newErrors.selectedTests = t("Please select at least one test");
        if (total <= 0) newErrors.total = t("Total amount must be greater than zero");
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 💾 Submit Handler
    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true)
            try {
                const payload = {
                    name: formData.packageName.trim(),
                    packageCode: formData.packageCode.trim(),
                    price: total,
                    includedTests: selectedTests.map(test => ({
                        testId: test.id,
                        testName: test.name,
                        testPrice: test.price
                    }))
                }
                const res = await addPackage(payload)
                if (res.data.success == true) {
                    toast.success(res.data.message)
                    navigate(`${process.env.PUBLIC_URL}/package-list`);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false)
            }

        }
    };

    // 🧭 Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
            <Breadcrumbs mainTitle={t("Add Package")} parent={t("Home")} subParent={t("Package List")} title={t("Add Package")} />
            <Container fluid={true}>
                <Row>
                    <Col className="h-100">
                        <Card className="mb-0">
                            <div className="card-body p-3 pt-3">
                                {/* Top Row: Package Name, Package Code, Test Selection */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold mb-1">{t("Package Name")} <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            name="packageName"
                                            value={formData.packageName}
                                            onChange={handleInputChange}
                                            className={`form-control rounded-3 ${errors.packageName ? "is-invalid" : ""}`}
                                            placeholder={t("Enter Package Name")}
                                        />
                                        {errors.packageName && <div className="invalid-feedback">{errors.packageName}</div>}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold mb-1">{t("Package Code")} <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            name="packageCode"
                                            value={formData.packageCode}
                                            onChange={handleInputChange}
                                            className={`form-control rounded-3 ${errors.packageCode ? "is-invalid" : ""}`}
                                            placeholder={t("Enter Package Code")}
                                        />
                                        {errors.packageCode && <div className="invalid-feedback">{errors.packageCode}</div>}
                                    </div>

                                    {/* Dropdown Section */}
                                    <div className="col-md-4" ref={dropdownRef}>
                                        <label className="form-label fw-semibold mb-1">{t("Select Tests")} <span className="text-danger">*</span></label>
                                        <div className="position-relative">
                                            <button
                                                type="button"
                                                className={`form-select text-start ${errors.selectedTests ? "is-invalid" : ""}`}
                                                onClick={() => setIsOpen(!isOpen)}
                                            >
                                                <span className={selectedTests.length > 0 ? "fw-medium" : "text-muted"}>
                                                    {selectedTests.length > 0 ? `${selectedTests.length} ${t("test(s) selected")}` : t("Select Test(s)...")}
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <div
                                                    className="position-absolute bg-white border rounded shadow-sm w-100 mt-1 custom-slim-scrollbar"
                                                    style={{ zIndex: 1050, maxHeight: "280px", overflowY: "auto" }}
                                                >
                                                    {/* Search */}
                                                    <div className="p-2 border-bottom sticky-top bg-white">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder={t("Search tests...")}
                                                            value={search}
                                                            onChange={handleSearch}
                                                            autoFocus
                                                        />
                                                    </div>

                                                    {/* Multi Select List */}
                                                    {options.length > 0 ? (
                                                        options.map((opt, index) => (
                                                            <label
                                                                key={index}
                                                                className="dropdown-item px-3 py-2 d-flex justify-content-between align-items-center"
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <div className="d-flex align-items-center me-2 text-truncate">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input me-2 flex-shrink-0"
                                                                        checked={selectedTests.some(test => test.id === opt.id)}
                                                                        onChange={() => handleCheckboxSelect(opt)}
                                                                    />
                                                                    <span className="text-truncate">{opt.name}</span>
                                                                </div>
                                                                <span className="badge bg-light text-dark fw-bold flex-shrink-0">{opt.price}</span>
                                                            </label>
                                                        ))
                                                    ) : (
                                                        <div className="text-muted px-3 py-2 text-center">{t("No results found")}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {errors.selectedTests && (
                                            <div className="text-danger tw-text-[0.875em] mt-1">{errors.selectedTests}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Main Area: Test Table (Left) + Total Amount & Actions (Right) */}
                                <div className="row g-3">
                                    {/* Left: Test Table */}
                                    <div className="col-lg-9 col-md-8">
                                        <div
                                            className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black custom-slim-scrollbar"
                                            style={{ maxHeight: "360px", overflowX: "auto", overflowY: "auto" }}
                                        >
                                            <Table className="mb-0 table-center table-bordered table-hover doctor-table bg-white text-dark" style={{ tableLayout: "fixed", minWidth: "560px", overflowX: "auto" }}>
                                                <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                                                    <tr>
                                                        <th className="text-center bg-primary text-white" style={{ width: "60px", background: '#1b255b', color: 'white' }}><b>{t("Sr.No")}</b></th>
                                                        <th className="bg-primary text-white text-start" style={{ width: "260px", background: '#1b255b', color: 'white' }}><b>{t("Test Name")}</b></th>
                                                        <th className="bg-primary text-white text-start" style={{ width: "140px", background: '#1b255b', color: 'white' }}><b>{t("Price")}</b></th>
                                                        <th className="text-center bg-primary text-white" style={{ width: "100px", background: '#1b255b', color: 'white' }}><b>{t("Action")}</b></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedTests.length > 0 ? (
                                                        selectedTests.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td className="text-center font-weight-bold" style={{ color: "black" }}>{index + 1}</td>
                                                                <td className="tw-font-medium text-dark text-start" style={{ color: "black", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.name}>
                                                                    {item.name}
                                                                </td>
                                                                <td className="fw-bold text-dark text-start" style={{ color: "black", whiteSpace: "nowrap" }}>{item.price}</td>
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDelete(index)}
                                                                        className="btn btn-sm btn-link text-danger p-0 d-inline-flex align-items-center justify-content-center"
                                                                        style={{ width: "28px", height: "28px" }}
                                                                        title={t("Delete test")}
                                                                    >
                                                                        <i className="bi bi-trash fs-6"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={4} className="tw-text-center py-3 text-muted fw-semibold">
                                                                {t("No Test Selected")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>

                                    {/* Right Side: Total Amount & Action Buttons */}
                                    <div className="col-lg-3 col-md-4">
                                        <div className="rounded p-3 border" style={{ backgroundColor: '#1b255b', color: 'white' }}>
                                            <label className="form-label fw-semibold text-white mb-1">{t("Total Amount")}</label>
                                            <input
                                                type="number"
                                                className={`form-control fw-bold ${errors.total ? "is-invalid" : ""}`}
                                                value={total}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setTotal(value === "" ? "" : Number(value));
                                                }}
                                                placeholder="0"
                                            />
                                            {errors.total && <div className="text-warning tw-text-[0.875em] mt-1">{errors.total}</div>}
                                        </div>

                                        <div className="d-flex flex-column gap-2 mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-primary w-100"
                                                onClick={handleSubmit}
                                            >
                                                {t("Create")}
                                            </button>
                                            
                                            <Link to={`${process.env.PUBLIC_URL}/package-list`} className="w-100">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary w-100"
                                                >
                                                    {t("Cancel")}
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
